import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (userId) => {
    if (!userId) {
      setUserProfile(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // PGRST116 = no rows returned (profile doesn't exist yet)
        if (error.code === 'PGRST116') {
          console.log('No profile found for user, creating one...');
          // Auto-create profile for old users or users without profiles
          await createUserProfile(userId);
          // Try fetching again
          const { data: newData, error: fetchError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (fetchError) {
            console.error('Error fetching profile after creation:', fetchError);
            setUserProfile(null);
          } else {
            setUserProfile(newData);
          }
        } else if (error.code === 'PGRST301' || error.status === 406 || error.message?.includes('406')) {
          // RLS policy issue - try to create profile anyway
          console.error('❌ RLS Policy Error (406) - Attempting to create profile...');
          console.error('Error details:', error);
          await createUserProfile(userId);
          // Try fetching one more time
          const { data: retryData, error: retryError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (retryError) {
            console.error('Still cannot fetch profile after creation. Run setup_user_profiles.sql in Supabase.');
            setUserProfile(null);
          } else {
            setUserProfile(retryData);
          }
        } else {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(data);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setUserProfile(null);
    }
  };

  const createUserProfile = async (userId) => {
    try {
      // Get user email and metadata from auth
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const email = authUser?.email || '';
      
      // Try to get nickname from user metadata first (set during signup)
      let defaultNickname = authUser?.user_metadata?.nickname;
      
      // If no nickname in metadata, generate from email
      if (!defaultNickname || defaultNickname.trim().length < 2) {
        defaultNickname = email.split('@')[0] || 'User';
      }
      
      // Ensure nickname meets length requirements
      if (defaultNickname.length < 2) {
        defaultNickname = 'User' + Math.floor(Math.random() * 1000);
      }
      if (defaultNickname.length > 20) {
        defaultNickname = defaultNickname.substring(0, 20);
      }
      
      defaultNickname = defaultNickname.trim();
      
      // Create profile with nickname from metadata or default
      const { error: createError } = await supabase
        .from('user_profiles')
        .insert([
          {
            id: userId,
            nickname: defaultNickname,
            email: email
          }
        ]);

      if (createError) {
        console.error('Error creating user profile:', createError);
        // If it's a duplicate key error, that's okay (race condition)
        if (createError.code !== '23505') {
          // If it's an RLS error, log it but don't throw
          if (createError.code === 'PGRST301' || createError.status === 406) {
            console.error('❌ Cannot create profile: RLS policy blocking. Run setup_user_profiles.sql in Supabase.');
          }
          throw createError;
        }
      } else {
        console.log('User profile created successfully');
      }
    } catch (err) {
      console.error('Error in createUserProfile:', err);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    
    return () => {
      mounted = false;
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      // Only set loading to false after we've processed the auth state
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata // Store nickname in user metadata
      }
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      // If login successful, update state immediately
      if (data?.session?.user && !error) {
        console.log('SignIn: Setting user state immediately');
        setSession(data.session);
        setUser(data.session.user);
        setLoading(false);
        
        // Fetch profile immediately
        await fetchUserProfile(data.session.user.id);
      }
      
      return { data, error };
    } catch (err) {
      console.error('Sign in error:', err);
      return { data: null, error: err };
    }
  };

  const signOut = async () => {
    try {
      // Clear profile first
      setUserProfile(null);
      setUser(null);
      setSession(null);
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
      }
      
      return { error };
    } catch (err) {
      console.error('Sign out exception:', err);
      return { error: err };
    }
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  };

  const value = {
    user,
    session,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    refreshProfile: () => user && fetchUserProfile(user.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

